// components/mobile/favorites.js
// Favorit dashboard versi mobile — v2 disimpan PER-AKUN di server
// (tabel `user_preferences`, key `favorites`), tidak lagi per-device.
// localStorage tetap dipakai HANYA sebagai cache agar UI tidak flicker saat
// mount; sumber kebenaran adalah DB, jadi menghapus cache browser tidak
// menghilangkan favorit.
// Nilai: array route key (mis. ['solman','aws_cost']).
import { useEffect, useState } from 'react';
import { api } from '../../context/AuthContext';

const LS_KEY   = 'mobile_favorites';
const PREF_KEY = 'favorites';
const EVT      = 'mobile-favorites-changed';

let hydrated  = false;   // sudah sinkron dari server pada sesi ini?
let inFlight  = null;    // promise hydrate berjalan (dedupe pemanggil paralel)
let saveTimer = null;    // debounce PUT (long-press berulang tidak spam server)

function sanitize(v) {
  return Array.isArray(v) ? v.filter(k => typeof k === 'string') : [];
}

// Cache lokal — sinkron, dipakai untuk render pertama.
export function getFavorites() {
  try { return sanitize(JSON.parse(localStorage.getItem(LS_KEY) || '[]')); }
  catch { return []; }
}

function writeCache(list) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(list)); } catch {}
}

function emit(list) {
  try { window.dispatchEvent(new CustomEvent(EVT, { detail: list })); } catch {}
}

// Payload yang sedang menunggu debounce. Disimpan terpisah supaya bisa
// di-flush saat logout — tanpa ini, isi timer yang di-clearTimeout hilang
// begitu saja.
let pendingList = null;

function pushToServer(list) {
  pendingList = list;
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    saveTimer = null; pendingList = null;
    api.put(`/api/user/preferences/${PREF_KEY}`, { value: list })
      .catch(err => console.warn('[favorites] gagal simpan ke server:', err?.message || err));
  }, 400);
}

// Kirim segera perubahan yang masih tertunda. Dipanggil saat logout: toggle
// favorit lalu logout dalam <400 ms dulu membuat PUT-nya hilang SENYAP karena
// resetFavoritesCache mem-clearTimeout tanpa mengirim apa pun.
export function flushFavorites() {
  if (!saveTimer) return;
  clearTimeout(saveTimer); saveTimer = null;
  const list = pendingList; pendingList = null;
  if (!list) return;
  api.put(`/api/user/preferences/${PREF_KEY}`, { value: list })
    .catch(err => console.warn('[favorites] flush gagal:', err?.message || err));
}

// Ambil favorit dari server. Bila server belum punya data tetapi cache lokal
// terisi (user lama, era localStorage) → migrasikan cache ke server sekali.
export function hydrateFavorites() {
  if (hydrated) return Promise.resolve(getFavorites());
  if (inFlight) return inFlight;
  inFlight = api.get(`/api/user/preferences/${PREF_KEY}`)
    .then(({ data }) => {
      const local  = getFavorites();
      const remote = data?.value;
      let next;
      if (remote === null || typeof remote === 'undefined') {
        next = local;
        if (next.length) pushToServer(next);   // migrasi one-shot
      } else {
        next = sanitize(remote);
      }
      hydrated = true;
      writeCache(next);
      emit(next);
      return next;
    })
    .catch(err => {
      // Offline / token expired → jalan dengan cache lokal, jangan kunci
      // `hydrated` supaya percobaan berikutnya masih bisa sinkron.
      console.warn('[favorites] gagal muat dari server:', err?.message || err);
      return getFavorites();
    })
    .finally(() => { inFlight = null; });
  return inFlight;
}

// Reset saat logout agar sesi user berikutnya tidak memakai cache orang lain.
// (AuthContext.logout memancarkan 'auth-logout' — tanpa import, hindari circular.)
export function resetFavoritesCache() {
  // Flush DULU, baru bersihkan. Urutannya penting dan bergantung pada
  // AuthContext yang memancarkan 'auth-logout' SEBELUM menghapus auth_token —
  // interceptor api membaca token dari localStorage, jadi kalau token sudah
  // hilang, PUT ini akan kena 401 dan datanya tetap lenyap.
  flushFavorites();
  hydrated = false;
  if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; }
  pendingList = null;
  try { localStorage.removeItem(LS_KEY); } catch {}
  emit([]);
}
try { window.addEventListener('auth-logout', resetFavoritesCache); } catch {}

export function isFavorite(key) {
  return getFavorites().includes(key);
}

// Toggle optimistik: cache + UI langsung berubah, PUT menyusul (debounced).
// Tetap sinkron & mengembalikan array terbaru — signature v1 dipertahankan.
export function toggleFavorite(key) {
  const cur  = getFavorites();
  const next = cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key];
  writeCache(next);
  emit(next);
  pushToServer(next);
  return next;
}

// Hook util sederhana tanpa dependency eksternal.
export function useFavorites() {
  const [favs, setFavs] = useState(getFavorites);
  useEffect(() => {
    const h = (e) => setFavs(sanitize(e.detail) || getFavorites());
    window.addEventListener(EVT, h);
    hydrateFavorites().then(list => setFavs(list));
    return () => window.removeEventListener(EVT, h);
  }, []);
  return [favs, toggleFavorite];
}
